import { useState, useEffect, useRef } from "react";
import { RadioGroup } from "@headlessui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Rating,
  Modal,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/Add";
import RemoveRoundedIcon from "@mui/icons-material/Remove";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import { TbRulerMeasure } from "react-icons/tb";
import { useDispatch, useSelector } from "react-redux";
import {
  findProductById,
} from "../../../../Redux/Customers/Product/Action";
import { addItemToCart } from "../../../../Redux/Customers/Cart/Action";
import {
  isInWishlist,
  addToWishlist,
  removeFromWishlist,
} from "../../../../utils/wishlist";
import { getAllReviews } from "../../../../Redux/Customers/Review/Action";
import api from "../../../../config/api";
import useSiteSettings from "../../../../hooks/useSiteSettings";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import HomeProductCard from "../../Home/HomeProductCard";
import { useCartDrawer } from "../../Cart/CartDrawerContext";
import SizeChartTop from "../../SizeChart/SizeChartTop";
import SizeChartBottom from "../../SizeChart/SizeChartBottom";
import { Skeleton, ProductDetailsSkeleton, ProductRailSkeleton } from "../../../../components/Skeleton";
import { usePageTitle } from "../../../../utils/pageTitle";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}
const prettify = (s = "") =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Fallback colour for a swatch when a variant has no image.
const SWATCH = {
  white: "#fff", cream: "#f3ead6", beige: "#e8dcc4", yellow: "#e0c24c", gold: "#c9a23a",
  orange: "#d2772e", red: "#b03030", maroon: "#7a2330", marun: "#7a2330", pink: "#e08bb0",
  purple: "#7a4f9e", blue: "#3b6fb0", navy: "#26324f", green: "#3f7d4f", brown: "#7a4a2b",
  grey: "#888", gray: "#888", black: "#222",
};
const swatchColor = (c = "") =>
  SWATCH[String(c).toLowerCase().trim()] || String(c).toLowerCase().trim() || "#ccc";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "16px",
  overflowY: "auto",
  height: "90vh",
  width: "80%",
  outline: "none",
};

const TRUST = [
  { Icon: LocalShippingOutlinedIcon, label: "Pan-India delivery" },
  { Icon: VerifiedOutlinedIcon, label: "Quality assured" },
  { Icon: ReplayRoundedIcon, label: "Easy exchange" },
];

export default function ProductDetails() {
  const [selectedSize, setSelectedSize] = useState();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { customersProduct } = useSelector((store) => store);
  const { productId, category } = useParams();
  const jwt = localStorage.getItem("jwt");
  const { auth } = useSelector((store) => store);
  const reviews = useSelector((store) => store.review?.reviews) || [];
  // Related products (same-category alternatives + accessories cross-sell).
  const [related, setRelated] = useState({ alternatives: [], completeTheLook: [] });
  // Related rails load separately from the product, so they get their own flag.
  const [relatedLoading, setRelatedLoading] = useState(true);
  // Whether THIS signed-in user may review THIS product. The API is the single
  // source of truth (it applies the same rules createReview enforces), so the
  // button is never shown for an action that would be rejected. Defaults to
  // false: a guest, a failed check or a still-loading check all hide it.
  const [canReview, setCanReview] = useState(false);
  const { openDrawer } = useCartDrawer();
  const siteSettings = useSiteSettings();
  // Admin-managed shipping note for the product page. Nothing is hardcoded —
  // the whole section is hidden unless the admin writes a note in
  // Settings → Shipping.
  const shippingInfo = siteSettings.productShippingInfo || "";

  // Real rating derived from the actual reviews (no fabricated values).
  const ratedReviews = reviews.filter((r) => Number(r?.rating) > 0);
  const avgRating = ratedReviews.length
    ? ratedReviews.reduce((sum, r) => sum + Number(r.rating), 0) /
      ratedReviews.length
    : 0;

  const [itemCount, setItemCount] = useState(1);
  const [inWishlist, setInWishlist] = useState(false);
  const [variants, setVariants] = useState([]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const decodedQueryString = decodeURIComponent(location.search);
  const searchParams = new URLSearchParams(decodedQueryString);
  let view_all = searchParams.get("view_all");

  const product = customersProduct.product;
  // Tab title: the product being viewed (ignore a stale product from the
  // previous page until this one has loaded).
  usePageTitle(product?._id === productId ? product?.title : "");
  const images = product?.images?.length ? product.images : [product?.imageUrl].filter(Boolean);
  const mainImg = images[activeImageIndex] || images[0];

  const handleSetActiveImage = (index) => setActiveImageIndex(index);

  // Swipe the main image on touch devices (the arrows are gone). Tracked with a
  // ref, not state, so moving a finger doesn't re-render on every touchmove.
  const touch = useRef({ x: 0, y: 0, tracking: false });
  const SWIPE_MIN = 40; // px — below this it's a tap, not a swipe

  const showPrev = () =>
    setActiveImageIndex((i) => (i === 0 ? images.length - 1 : i - 1));
  const showNext = () =>
    setActiveImageIndex((i) => (i === images.length - 1 ? 0 : i + 1));

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, tracking: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.tracking || images.length < 2) return;
    touch.current.tracking = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    // Ignore mostly-vertical gestures so page scrolling still works normally.
    if (Math.abs(dx) < SWIPE_MIN || Math.abs(dx) <= Math.abs(dy)) return;
    if (dx < 0) showNext();
    else showPrev();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // No sign-in required: a signed-out shopper's item goes to the local guest
    // cart and is merged into their account when they sign in (see
    // mergeGuestCart). Sign-in is only needed at checkout.
    if (product?.isSizesAvailable && !selectedSize) {
      toast.error("Please select a size.");
      return;
    }
    const data = { productId, size: selectedSize?.name, quantity: itemCount };
    const result = await dispatch(addItemToCart({ data, jwt }));
    // Only confirm/open the mini-cart on a real success; stay on the page so the
    // shopper can keep browsing (and see the related / cross-sell rows).
    if (result?.success === false) {
      toast.error(result.error || "Could not add to cart. Please try again.");
      return;
    }
    toast.success("Added to cart");
    openDrawer();
  };

  // Phones/tablets: a bottom bar with price + Add to Cart once the main button
  // has scrolled off the top of the screen, so buying never needs a scroll back.
  const formRef = useRef(null);
  const buyRowRef = useRef(null);
  const [showStickyBuy, setShowStickyBuy] = useState(false);
  useEffect(() => {
    // Position check on scroll rather than an IntersectionObserver: the
    // observer only fires when the button crosses the screen edge, so a fast
    // fling straight past it (never on screen) left the bar hidden.
    let frame = 0;
    const update = () => {
      frame = 0;
      const el = buyRowRef.current;
      setShowStickyBuy(Boolean(el) && el.getBoundingClientRect().bottom < 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [product?._id]);

  const stickyAddToCart = (e) => {
    if (product?.isSizesAvailable && !selectedSize) {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      toast.error("Please select a size.");
      return;
    }
    handleSubmit(e);
  };

  // Colour variants (same item in other colours), if the admin linked any.
  useEffect(() => {
    api
      .get(`/api/products/variants/${productId}`)
      .then(({ data }) => setVariants(data || []))
      .catch(() => setVariants([]));
  }, [productId]);

  // Reflect whether this product is already in the wishlist. Guests have one
  // too (kept in this browser, merged into their account on sign-in).
  useEffect(() => {
    isInWishlist(productId)
      .then(setInWishlist)
      .catch(() => {});
  }, [productId, auth.user]);

  const toggleWishlist = async () => {
    try {
      if (inWishlist) {
        await removeFromWishlist(productId);
        setInWishlist(false);
        toast.info("Removed from wishlist");
      } else {
        await addToWishlist(productId);
        setInWishlist(true);
        toast.success("Added to wishlist");
      }
    } catch (e) {
      toast.error("Something went wrong");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImageIndex(0);
    dispatch(findProductById({ productId, jwt }));
    dispatch(getAllReviews(productId));
    // Related products: same-category alternatives + accessories cross-sell.
    api
      .get(`/api/products/related/${productId}`)
      .then(({ data }) => {
        setRelatedLoading(false);
        setRelated({
          alternatives: data?.alternatives || [],
          completeTheLook: data?.completeTheLook || [],
        });
      })
      .catch(() => {
        setRelatedLoading(false);
        setRelated({ alternatives: [], completeTheLook: [] });
      });

    // Only a signed-in user can have a purchase history to check.
    if (!jwt) {
      setCanReview(false);
      return;
    }
    setRelatedLoading(true);
    setCanReview(false); // reset while the check for the new product is in flight
    api
      .get(`/api/reviews/can-review/${productId}`)
      .then(({ data }) => setCanReview(Boolean(data?.canReview)))
      .catch(() => setCanReview(false));
  }, [dispatch, productId, jwt]);

  const handleSizeIncrease = (p) => {
    if (p?.isSizesAvailable && !selectedSize) {
      toast.error("Select a Size!");
      return;
    } else {
      if (p?.sizes?.find((item) => item?.name === selectedSize?.name)?.quantity === itemCount) {
        toast.error("Quantity Not Available!");
        return;
      } else if (!p?.isSizesAvailable && p.quantity === itemCount) {
        toast.error("Quantity Not Available!");
        return;
      }
    }
    setItemCount(itemCount + 1);
  };

  useEffect(() => {
    setItemCount(1);
  }, [selectedSize]);

  const sizeChangeHanlder = (e) => {
    const { quantity } = e;
    if (quantity > 0) setSelectedSize(e);
    else setSelectedSize("");
  };

  const similar = related.alternatives || [];
  const completeTheLook = related.completeTheLook || [];

  // The redux product slice is shared, so navigating from one product to
  // another briefly leaves the PREVIOUS product in the store. Comparing ids
  // means the skeleton also covers that swap, not just the very first load —
  // otherwise you'd see the old product's photos under the new product's URL.
  const productLoaded = product && String(product._id) === String(productId);
  if (!productLoaded) {
    return (
      <div className="min-h-screen bg-[#fffbf2]">
        <ProductDetailsSkeleton />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fffbf2]">
      <ToastContainer position="top-right" autoClose={4000} theme="light" />

      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
        {/* breadcrumb */}
        <p className="mb-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#b28585]">
          {prettify(category)} <span className="text-[#cbb]">›</span>{" "}
          <span className="text-[#8a7575]">{product?.title}</span>
        </p>

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          {/* Gallery */}
          <div className="flex flex-col-reverse gap-4 sm:flex-row">
            {/* thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 sm:flex-col">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => handleSetActiveImage(i)}
                    className={classNames(
                      "h-20 w-16 overflow-hidden rounded-lg border-2 transition",
                      i === activeImageIndex ? "border-[#b28585]" : "border-[#eadcc0] hover:border-[#cbb]"
                    )}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover object-top" />
                  </button>
                ))}
              </div>
            )}
            {/* main image — swipe to change (no arrows) */}
            <div className="relative flex-1">
              <div
                className="aspect-[3/4] overflow-hidden rounded-2xl border border-[#eadcc0] bg-white"
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                {mainImg && (
                  <img
                    src={mainImg}
                    alt={product?.title}
                    // Stops the browser turning the swipe into an image drag,
                    // which would cancel the gesture on some devices.
                    draggable={false}
                    className="h-full w-full select-none object-cover object-center"
                  />
                )}
              </div>

              {/* Dots: with the arrows removed this is the only cue on mobile
                  that there is more than one photo. Tappable as a fallback for
                  anyone who doesn't discover the swipe. Thumbnails already
                  serve this purpose from sm upwards, so keep it small-screen. */}
              {images.length > 1 && (
                <div className="mt-3 flex justify-center gap-2 sm:hidden">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Show image ${i + 1} of ${images.length}`}
                      aria-current={i === activeImageIndex}
                      onClick={() => handleSetActiveImage(i)}
                      className={`h-2 rounded-full transition-all ${
                        i === activeImageIndex
                          ? "w-6 bg-[#b28585]"
                          : "w-2 bg-[#e0cfcf]"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Info */}
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-[#b28585]">
              {product?.brand}
            </p>
            <h1 className="mt-1 font-display text-3xl font-bold leading-tight text-[#511d00] sm:text-4xl">
              {product?.title}
            </h1>

            <div className="mt-2 flex items-center gap-2">
              <Rating value={avgRating} precision={0.5} size="small" readOnly sx={{ color: "#e0b65c" }} />
              <a href="#customer-reviews" className="text-sm text-[#8a7575] hover:text-[#5e3e3e]">
                {ratedReviews.length > 0
                  ? `${avgRating.toFixed(1)} · ${reviews.length} ${
                      reviews.length === 1 ? "review" : "reviews"
                    }`
                  : "No reviews yet"}
              </a>
            </div>

            {/* price */}
            <div className="mt-5 flex items-end gap-3">
              <span className="text-3xl font-bold text-[#511d00]">
                ₹{Number(product?.discountedPrice || 0).toLocaleString("en-IN")}
              </span>
              {product?.price > product?.discountedPrice && (
                <span className="text-lg text-[#b3a3a3] line-through">
                  ₹{Number(product?.price || 0).toLocaleString("en-IN")}
                </span>
              )}
              {product?.discountPercent > 0 && (
                <span className="pb-1 text-sm font-semibold text-[#2f9e69]">
                  {product?.discountPercent}% off
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[#8a7575]">Inclusive of all taxes</p>

            {((product?.color && variants.length <= 1) || product?.fabric) && (
              <div className="mt-4 space-y-1 text-sm text-[#5e3e3e]">
                {/* Colour shown here unless the colour-variant swatches below
                    already display it (avoids showing it twice). */}
                {product?.color && variants.length <= 1 && (
                  <p>
                    <span className="font-semibold text-[#511d00]">Colour:</span>{" "}
                    <span className="capitalize">{prettify(product.color)}</span>
                  </p>
                )}
                {product?.fabric && (
                  <p>
                    <span className="font-semibold text-[#511d00]">Fabric:</span>{" "}
                    <span className="capitalize">{prettify(product.fabric)}</span>
                  </p>
                )}
              </div>
            )}

            <div className="my-6 h-px bg-[#eadcc0]" />

            <form onSubmit={handleSubmit} ref={formRef} className="scroll-mt-24">
              {/* colour variants */}
              {variants.length > 1 && (
                <div className="mb-6">
                  <h3 className="mb-3 text-sm font-semibold text-[#511d00]">
                    Colour:{" "}
                    <span className="font-normal capitalize text-[#5e3e3e]">
                      {prettify(product?.color || "")}
                    </span>
                  </h3>
                  <div className="flex flex-wrap gap-2.5">
                    {variants.map((v) => {
                      const active = v._id === productId;
                      return (
                        <button
                          type="button"
                          key={v._id}
                          title={prettify(v.color)}
                          onClick={() =>
                            !active &&
                            navigate(`/product/${v._id}/${v.category || category}`)
                          }
                          className={classNames(
                            "relative h-10 w-10 overflow-hidden rounded-full transition focus:outline-none",
                            active
                              ? "ring-2 ring-[#5e3e3e] ring-offset-2"
                              : "ring-1 ring-[#d8c3c3] ring-offset-2 hover:ring-[#b28585]"
                          )}
                          style={{ backgroundColor: swatchColor(v.color) }}
                        >
                          {v.image && (
                            <img
                              src={v.image}
                              alt={v.color}
                              className="h-full w-full object-cover"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {product?.isSizesAvailable && (
                <p
                  className="mb-3 flex w-max cursor-pointer items-center text-sm font-medium text-[#b28585] underline-offset-2 hover:underline"
                  onClick={handleOpen}
                >
                  <TbRulerMeasure className="mr-1" /> Size Chart
                </p>
              )}

              {/* sizes */}
              {product?.isSizesAvailable && (
                <div>
                  <h3 className="mb-3 text-sm font-semibold text-[#511d00]">
                    Select Size
                  </h3>
                  <RadioGroup value={selectedSize} onChange={(e) => sizeChangeHanlder(e)}>
                    <div className="flex flex-wrap gap-3">
                      {product?.sizes.map((size) => (
                        <RadioGroup.Option
                          key={size.name}
                          value={size}
                          disabled={Number(size.quantity) === 0}
                          className={({ checked }) =>
                            classNames(
                              "flex h-11 min-w-[3rem] cursor-pointer items-center justify-center rounded-lg border px-3 text-sm font-semibold uppercase transition focus:outline-none",
                              Number(size.quantity) <= 0
                                ? "cursor-not-allowed border-[#eadcc0] bg-[#f3ece0] text-[#cbb] line-through"
                                : selectedSize?.name === size.name
                                ? "border-transparent bg-[#5e3e3e] text-white shadow"
                                : "border-[#d8c3c3] text-[#5e3e3e] hover:border-[#b28585]"
                            )
                          }
                        >
                          <RadioGroup.Label as="span">{size.name}</RadioGroup.Label>
                        </RadioGroup.Option>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              )}

              {/* quantity */}
              <div className="mt-6">
                <h3 className="mb-3 text-sm font-semibold text-[#511d00]">Quantity</h3>
                <div className="flex w-max items-center rounded-full border border-[#d8c3c3] bg-white">
                  <IconButton aria-label="Decrease quantity" onClick={() => setItemCount(itemCount - 1)} disabled={itemCount <= 1} sx={{ color: "#b28585" }}>
                    <RemoveRoundedIcon />
                  </IconButton>
                  <span className="min-w-[2.5rem] text-center font-semibold text-[#511d00]">
                    {itemCount}
                  </span>
                  <IconButton aria-label="Increase quantity" onClick={() => handleSizeIncrease(product)} sx={{ color: "#b28585" }}>
                    <AddRoundedIcon />
                  </IconButton>
                </div>
              </div>

              <div ref={buyRowRef} className="mt-4 flex items-center gap-3">
                <Button
                  variant="contained"
                  type="submit"
                  startIcon={<ShoppingBagOutlinedIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderRadius: "999px", fontWeight: 700, fontSize: "1rem" }}
                >
                  Add To Cart
                </Button>
                <IconButton
                  onClick={toggleWishlist}
                  aria-label="wishlist"
                  sx={{
                    border: "1px solid #d8c3c3",
                    borderRadius: "999px",
                    width: 52,
                    height: 52,
                    color: inWishlist ? "#b85c5c" : "#b28585",
                    "&:hover": { borderColor: "#b28585", bgcolor: "rgba(178,133,133,0.08)" },
                  }}
                >
                  {inWishlist ? <FavoriteRoundedIcon /> : <FavoriteBorderRoundedIcon />}
                </IconButton>
              </div>
            </form>

            {/* trust badges */}
            <div className="mt-6 grid grid-cols-3 gap-2 rounded-2xl border border-[#eadcc0] bg-white p-4">
              {TRUST.map(({ Icon, label }) => (
                <div key={label} className="flex flex-col items-center gap-1 text-center">
                  <Icon sx={{ color: "#b28585", fontSize: 24 }} />
                  <span className="text-[11px] font-medium text-[#5e3e3e]">{label}</span>
                </div>
              ))}
            </div>

            {/* description + shipping */}
            <div className="mt-8">
              <h3 className="mb-2 font-semibold text-[#511d00]">Description</h3>
              <p className="text-sm leading-relaxed text-[#5e3e3e]">
                {product?.description}
              </p>

              {shippingInfo && (
                <Accordion
                  disableGutters
                  sx={{ bgcolor: "transparent", boxShadow: "none", borderTop: "1px solid #eadcc0", mt: 3, "&:before": { display: "none" } }}
                >
                  <AccordionSummary expandIcon={<ArrowDropDownIcon sx={{ color: "#b28585" }} />} sx={{ px: 0 }}>
                    <span className="font-semibold text-[#511d00]">Shipping Information</span>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0 }}>
                    <p className="text-sm leading-relaxed text-[#5e3e3e]">
                      {shippingInfo}
                    </p>
                  </AccordionDetails>
                </Accordion>
              )}
            </div>
          </div>
        </div>

        {/* Customer reviews */}
        <section id="customer-reviews" className="mt-16 scroll-mt-24">
          <div className="mb-6 flex items-center justify-between gap-4">
            <h2 className="font-display text-3xl font-bold text-[#511d00]">
              Customer Reviews
            </h2>
            {/* Shown only to a customer who has actually received this product
                and hasn't reviewed it yet — the API decides. */}
            {canReview && (
              <button
                onClick={() => navigate(`/account/rate/${productId}`)}
                className="shrink-0 rounded-full bg-[#5e3e3e] px-5 py-2.5 text-sm font-bold text-white transition hover:bg-[#4a3020]"
              >
                Write a Review
              </button>
            )}
          </div>

          {reviews.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#eadcc0] bg-white px-6 py-10 text-center">
              <p className="font-display text-xl text-[#5e3e3e]">
                No reviews yet
              </p>
              <p className="mt-1 text-sm text-[#8a7575]">
                Be the first to share your experience with this piece.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {reviews.filter(Boolean).map((r) => {
                const name =
                  `${r?.user?.firstName || ""} ${r?.user?.lastName || ""}`.trim() ||
                  "Verified Buyer";
                const initial = (name[0] || "A").toUpperCase();
                const dateStr = r?.createdAt
                  ? new Date(r.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })
                  : "";
                return (
                  <div
                    key={r?._id}
                    className="rounded-2xl border border-[#eadcc0] bg-white p-5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#5e3e3e] font-display text-lg font-bold text-[#f1e7da]">
                        {initial}
                      </div>
                      <div>
                        <p className="font-semibold text-[#511d00]">{name}</p>
                        <div className="flex items-center gap-2">
                          <VerifiedOutlinedIcon
                            sx={{ color: "#2f9e69", fontSize: 14 }}
                          />
                          <span className="text-xs text-[#8a7575]">
                            Verified Buyer{dateStr ? ` · ${dateStr}` : ""}
                          </span>
                        </div>
                      </div>
                    </div>
                    {Number(r?.rating) > 0 && (
                      <Rating
                        value={Number(r.rating)}
                        readOnly
                        size="small"
                        sx={{ color: "#e0b65c", mt: 1.5 }}
                      />
                    )}
                    <p className="mt-2 text-sm leading-relaxed text-[#5e3e3e]">
                      {r?.review}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Related rails load after the product, so hold their space */}
        {relatedLoading && (
          <section className="mt-16">
            <Skeleton className="mb-6 h-8 w-64" />
            <ProductRailSkeleton count={5} />
          </section>
        )}

        {/* Complete the Look — accessories cross-sell (garment pages only) */}
        {completeTheLook.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-1 font-display text-3xl font-bold text-[#511d00]">
              Complete the Look
            </h2>
            <p className="mb-6 text-sm text-[#8a7575]">
              Accessories to finish your ensemble.
            </p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
              {completeTheLook.map((item) => (
                <HomeProductCard key={item._id} product={item} category={category} view_all={view_all} />
              ))}
            </div>
          </section>
        )}

        {/* You May Also Like — same-category alternatives */}
        {similar.length > 0 && (
          <section className="mt-16">
            <h2 className="mb-6 font-display text-3xl font-bold text-[#511d00]">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
              {similar.map((item) => (
                <HomeProductCard key={item._id} product={item} category={category} view_all={view_all} />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky buy bar (below lg). The spacer keeps it from covering the
          footer's last lines. */}
      {product?._id === productId && (
        <>
          <div className="h-20 lg:hidden" aria-hidden="true" />
          <div
            className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#eadcc0] bg-white/95 px-4 pt-3 shadow-[0_-6px_20px_rgba(94,62,62,0.10)] backdrop-blur transition-transform duration-300 lg:hidden ${
              showStickyBuy ? "translate-y-0" : "pointer-events-none translate-y-full"
            }`}
            style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
            aria-hidden={!showStickyBuy}
          >
            <div className="mx-auto flex max-w-xl items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs text-[#8a7575]">{product?.title}</p>
                <p className="flex items-baseline gap-2">
                  <span className="text-lg font-bold text-[#511d00]">
                    ₹{Number(product?.discountedPrice || 0).toLocaleString("en-IN")}
                  </span>
                  {product?.price > product?.discountedPrice && (
                    <span className="text-xs text-[#b3a3a3] line-through">
                      ₹{Number(product?.price || 0).toLocaleString("en-IN")}
                    </span>
                  )}
                </p>
              </div>
              <Button
                variant="contained"
                onClick={stickyAddToCart}
                startIcon={<ShoppingBagOutlinedIcon />}
                tabIndex={showStickyBuy ? 0 : -1}
                sx={{ py: 1.2, px: 2.5, borderRadius: "999px", fontWeight: 700, whiteSpace: "nowrap" }}
              >
                Add To Cart
              </Button>
            </div>
          </div>
        </>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              display: "flex",
              justifyContent: "flex-end",
              bgcolor: "#fff",
              px: 1.5,
              py: 1,
              borderBottom: "1px solid #eadcc0",
            }}
          >
            <IconButton aria-label="Close" onClick={handleClose} sx={{ color: "#5e3e3e" }}>
              <CloseRoundedIcon />
            </IconButton>
          </Box>
          {product?.thirdLavelCategory?.wearType === "bottom" ||
          ["trousers", "jeans"].includes(product?.thirdLavelCategory?.name) ? (
            <SizeChartBottom />
          ) : (
            <SizeChartTop />
          )}
        </Box>
      </Modal>
    </div>
  );
}
